import { ChevronRight } from "lucide-react";

/**
 * HOME — "This week": the smallest truthful orientation to what the team has
 * put in focus, and deliberately NOT a second Team Direction surface.
 *
 * WHY IT REPLACED THE TEAM DIRECTION SHORTCUT. The old card was a feature
 * shortcut: it said "Team Direction" and sent you elsewhere to find out what
 * that meant. Home now answers a question instead — what matters this week —
 * and the answer is the focused direction titles themselves. Everything about
 * managing direction (creating, editing, archiving, focusing, unfocusing,
 * notes, relevance marks) stays on the Team page, which owns that workspace.
 *
 * WHAT IT MAY NEVER SHOW, and why this list is written down rather than
 * assumed: this card sits on the personal surface, so it must never become a
 * place where team state is inferred about people. No counts of any kind ("3
 * priorities", "N focused", "X of Y"), no member names, no owner or author
 * identity, no participation, no completion, no manager state, no relevance
 * marks, no private-link state, no momentum. It takes plain strings and a
 * navigation callback, so it CANNOT render any of those — the component has no
 * access to them by construction, which is a stronger guarantee than a rule.
 *
 * READ-ONLY. There is no focus mutation on Home; the only affordance is
 * navigation into the team workspace that owns the data.
 *
 * ABSENT RATHER THAN EMPTY. With no focus set the card renders nothing at all.
 * An empty heading would be a worse answer than no answer, and /team stays
 * reachable from the tab bar regardless.
 *
 * THE HEADING IS A PROP, NOT A LITERAL. Home is week-NAVIGABLE, and this card
 * originally hardcoded "This week" — so paging back rendered last week's focus
 * under a present-tense claim, directly below a WorkWeekStrip that correctly
 * read "Last week". The label is now the same `weekContextLabel` Home already
 * computes for that strip, so the two cannot disagree and no date arithmetic is
 * duplicated here.
 */
export interface ThisWeekFocusCardProps {
  isAr: boolean;
  /**
   * The week actually in view, already localized by the page — "This week",
   * "Last week", "Next week" or a date range. Passed in rather than derived so
   * this card owns no date semantics and cannot contradict the week strip.
   */
  weekLabel: string;
  /** Focused team-direction titles, already resolved and ordered by the page. */
  titles: string[];
  /**
   * OPTIONAL, OWNER-ONLY. The CALLER'S OWN personal priority titles connected
   * to each focused direction, INDEX-ALIGNED with `titles` — entry i belongs to
   * titles[i]. Positional rather than keyed, so this card still never receives
   * an id it could correlate with anything, and no user-authored string is ever
   * used as an object key.
   *
   * WHY THIS IS STILL PLAIN STRINGS. The card's guarantee is that it cannot
   * render counts, member names, participation, relevance or link state because
   * it has no access to them. That holds: this is the viewer's own priority
   * titles and nothing else, joined on Home from reads Home already performs
   * for MyPrioritiesCard. No new endpoint, no grouped read, no other person's
   * data can reach this prop.
   *
   * Omit it and the card renders exactly as it did before.
   */
  connectedByIndex?: string[][];
  onOpenTeam: () => void;
}

/** Orientation, not a list. Two is enough to know where the week is pointed. */
const MAX_TITLES = 2;

export function ThisWeekFocusCard({
  isAr,
  weekLabel,
  titles,
  connectedByIndex,
  onOpenTeam,
}: ThisWeekFocusCardProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const shown = titles.slice(0, MAX_TITLES);
  if (shown.length === 0) return null;

  return (
    <section
      data-testid="home-this-week"
      className="mt-2.5 rounded-3xl border border-border/60 bg-card p-4 shadow-sm"
    >
      <h2 className="text-sm font-semibold text-foreground">{weekLabel}</h2>
      <ul className="mt-2 space-y-1.5">
        {shown.map((title, i) => (
          <li key={i} className="min-w-0">
            <p
              data-testid="home-this-week-focus"
              // Titles are user-authored shared text of unbounded length. Same
              // mobile-safe treatment MyPrioritiesCard already uses for owner
              // text: wrap long words rather than overflow the card, and clamp so
              // one very long title cannot push the rest of Home off screen.
              className="min-w-0 break-words line-clamp-2 text-sm text-muted-foreground"
            >
              {title}
            </p>
            {/* OWNER-ONLY. The private half of the week: which of the viewer's
                OWN priorities they connected to this focused direction. Never
                a count, never anyone else's work, never link state about
                another person. A priority connected to two focused directions
                appears under each, which is the truthful reading. */}
            {(connectedByIndex?.[i] ?? []).length > 0 ? (
              <div className="mt-1 ms-3 border-s border-border/60 ps-2.5">
                <p
                  className="text-[11px] font-medium text-muted-foreground"
                  data-testid="home-this-week-connected-label"
                >
                  {t("Your connected work", "عملك المرتبط")}
                </p>
                <ul className="mt-0.5 space-y-0.5">
                  {(connectedByIndex?.[i] ?? []).map((own, j) => (
                    <li
                      key={j}
                      data-testid="home-this-week-connected"
                      className="min-w-0 break-words line-clamp-2 text-xs text-muted-foreground"
                    >
                      {own}
                    </li>
                  ))}
                </ul>
                <p
                  className="mt-0.5 text-[11px] text-muted-foreground"
                  data-testid="home-this-week-connected-private"
                >
                  {t("Only you can see this.", "أنت وحدك ترى هذا.")}
                </p>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid="home-this-week-open-team"
        onClick={onOpenTeam}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-xs hover:bg-accent/50"
      >
        {t("View team", "عرض الفريق")}
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </section>
  );
}
